/*
 * Copyright (c) 2020. American Express Company
 */

// theirs
import React, { ChangeEvent, PureComponent, ReactComponentElement } from 'react';
import * as _ from 'lodash';

// ours
import Anchor from '../atomic/atoms/anchor/Anchor';
import ReportDrawer from './ReportDrawer';
import { ReportType } from 'src/types/report-type.enum';
import LiteralsService from '../../services/literals/LiteralsService';
import FormatService from '../../services/format.service';
import NavigationService from 'src/services/navigation.service';
import { NetWorthListModel, NetWorthType } from 'src/types/NetWorthListModel';
import { BaseAssetModel, DebtReportModel } from 'src/types/asset-debt.model';
import { AdjustmentModel } from 'src/types/adjusment.model';
import ReportService from '../../services/report.service';
import { ReportViewModel } from 'src/types/view-models/report.view.model';
import { MonteCarloEstimate } from 'src/types/monte-carlo.enum';

// styles
import styles from './NetWorth.module.scss';
import Typography from '../typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import Selectors from 'src/utils/selectors';

interface NetWorthDrawerState {
    monteCarlo: MonteCarloEstimate;
}

export default class NetWorthDrawer extends PureComponent<ReportViewModel, NetWorthDrawerState> {
    public override state = {
        monteCarlo: MonteCarloEstimate.AVERAGE
    };

    public override render(): ReactComponentElement<any> {
        // assets
        const asset: number = ReportService.getAsset(this.state.monteCarlo, this.props.drawerProps.cashFlowDetails);
        const assets: BaseAssetModel[] = _.get(this, 'props.drawerProps.cashFlowDetails.assets.bases');
        const assetAdjustments: AdjustmentModel[] = _.get(this, 'props.drawerProps.cashFlowDetails.assets.adjustments');
        const afterAsset: number = asset;

        // debts
        const debts: DebtReportModel = _.get(this, 'props.drawerProps.cashFlowDetails.debts');
        const afterDebt: number = debts.amount;

        const goToAssetDebtDrawer: () => void = () => {
            void NavigationService.goTo(
                this.props.drawerProps.history,
                `/show/assets/${this.props.drawerProps.cashFlowIndex}`,
                'Assets and Debts'
            );
        };

        // networth
        const networthList: NetWorthListModel[] = [
            {
                name: LiteralsService.getLiteral('NetWorthDrawer.yourAssets'),
                amount: afterAsset,
                detail:
                    assets.length + assetAdjustments.length === 1
                        ? '1 asset'
                        : LiteralsService.getLiteral('NetWorthDrawer.assetsCount', [
                              assets.length + assetAdjustments.length
                          ]),
                type: NetWorthType.ASSETS,
                key: 'networth-0',
                onClick: goToAssetDebtDrawer
            },
            {
                name: LiteralsService.getLiteral('NetWorthDrawer.yourDebts'),
                amount: afterDebt,
                detail:
                    debts.bases.length === 1
                        ? '1 debt'
                        : LiteralsService.getLiteral('NetWorthDrawer.debtsCount', [debts.bases.length]),
                type: NetWorthType.DEBTS,
                key: 'networth-1',
                onClick: goToAssetDebtDrawer
            }
        ];

        return (
            <ReportDrawer
                drawerProps={this.props.drawerProps}
                title={LiteralsService.getLiteral('NetWorthDrawer.reportTitle', [this.props.drawerProps.dateString])}
                reportType={ReportType.NETWORTH}
                monteCarlo={this.state.monteCarlo}
                handleClick={this.handleClick.bind(this)}
            >
                <div className={styles.netWorthTable} data-cy={Selectors.PlanAhead.NETWORTH_TABLE}>
                    {networthList.map((netWorth) => {
                        return this.getRowItem(netWorth);
                    })}
                </div>
            </ReportDrawer>
        );
    }

    private getRowItem(netWorth: NetWorthListModel): JSX.Element {
        return (
            <div className={styles.rowItem} key={netWorth.key}>
                <div className={styles.left}>
                    <Anchor
                        onClick={netWorth.onClick}
                        typographyProps={{
                            className: styles.rowHeading,
                            variant: TypographyVariants.BODY
                        }}
                    >
                        {netWorth.name}
                    </Anchor>
                    <Typography variant={TypographyVariants.CAPTION}>{netWorth.detail}</Typography>
                </div>
                <div className={styles.right}>
                    <Typography variant={TypographyVariants.BODY_BOLD}>
                        {FormatService.format(netWorth.amount)}
                    </Typography>
                </div>
            </div>
        );
    }

    private async handleClick(event: ChangeEvent<HTMLInputElement>): Promise<void> {
        this.setState({
            monteCarlo: event.target.value as MonteCarloEstimate
        });
    }
}
